'use client';

import { useState } from 'react';
import { theme, sectionLabel } from '../theme';
import { Container, SectionLabel } from '../primitives';
import { Reveal } from '../reveal';
import type { Strings } from '../strings';

export function FAQ({ s }: { s: Strings }) {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section
      style={{
        background: theme.c.bgAlt,
        padding: '120px 0',
        borderTop: `1px solid ${theme.c.border}`,
      }}
    >
      <Container>
        <Reveal
          className="lp-faq-head"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr',
            gap: 48,
            marginBottom: 56,
          }}
        >
          <SectionLabel>{sectionLabel(s.faq.label)}</SectionLabel>
          <div>
            <h2
              style={{
                fontFamily: theme.fonts.display,
                fontSize: 'clamp(36px, 4.5vw, 64px)',
                fontWeight: 600,
                letterSpacing: -1.5,
                lineHeight: 1.02,
                color: theme.c.fg,
                margin: 0,
              }}
            >
              {s.faq.title}
            </h2>
            <p
              style={{
                marginTop: 20,
                fontFamily: theme.fonts.body,
                fontSize: 16,
                lineHeight: 1.55,
                color: theme.c.fgMuted,
                maxWidth: 720,
              }}
            >
              {s.faq.introA}
              <a
                href="mailto:info@locusly.pt"
                style={{
                  color: theme.c.accent,
                  textDecoration: 'underline',
                  textDecorationThickness: 1,
                  textUnderlineOffset: 3,
                }}
              >
                info@locusly.pt
              </a>
              {s.faq.introB}
            </p>
          </div>
        </Reveal>

        <Reveal
          as="ul"
          delay={120}
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
            background: theme.c.surface,
            border: `1px solid ${theme.c.border}`,
            borderRadius: theme.radii.lg,
            overflow: 'hidden',
          }}
        >
          {s.faq.items.map((item, i) => {
            const isOpen = open === i;
            return (
              <li
                key={i}
                style={{
                  borderBottom:
                    i === s.faq.items.length - 1 ? 'none' : `1px solid ${theme.c.border}`,
                  background: isOpen ? 'rgba(200,85,61,0.05)' : 'transparent',
                  transition: 'background .2s ease',
                }}
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="lp-faq-trigger"
                  style={{
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '22px 28px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 18,
                    textAlign: 'left',
                    fontFamily: theme.fonts.display,
                    fontSize: 18,
                    fontWeight: 500,
                    color: theme.c.fg,
                    letterSpacing: -0.3,
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: theme.fonts.mono,
                        fontSize: 11,
                        letterSpacing: 1.3,
                        textTransform: 'uppercase',
                        color: isOpen ? theme.c.accent : theme.c.fgFaint,
                      }}
                    >
                      0{i + 1}
                    </span>
                    <span>{item.q}</span>
                  </span>
                  <span
                    aria-hidden="true"
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 999,
                      border: `1px solid ${isOpen ? theme.c.accent : theme.c.borderStrong}`,
                      background: isOpen ? theme.c.accent : theme.c.surfaceAlt,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: theme.fonts.display,
                      fontSize: 22,
                      lineHeight: 1,
                      color: isOpen ? theme.c.accentFg : theme.c.fg,
                      transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                      transition:
                        'transform .22s ease, background .22s ease, border-color .22s ease, color .22s ease',
                      boxShadow: isOpen
                        ? '0 12px 24px -14px rgba(200,85,61,0.55)'
                        : 'inset 0 1px 0 rgba(255,255,255,0.55)',
                      flex: '0 0 auto',
                      marginTop: 2,
                    }}
                  >
                    +
                  </span>
                </button>
                <div
                  style={{
                    maxHeight: isOpen ? 320 : 0,
                    overflow: 'hidden',
                    transition: 'max-height .25s ease',
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      padding: '0 28px 28px',
                      fontFamily: theme.fonts.body,
                      fontSize: 15,
                      lineHeight: 1.55,
                      color: theme.c.fgMuted,
                      maxWidth: 760,
                    }}
                  >
                    {item.a}
                  </p>
                </div>
              </li>
            );
          })}
        </Reveal>
      </Container>

      <style>{`
        .lp-faq-trigger {
          transition: transform .18s ease, color .18s ease;
        }

        .lp-faq-trigger:hover {
          transform: translateX(4px);
        }

        .lp-faq-trigger:focus-visible {
          outline: none;
          box-shadow: inset 0 0 0 2px ${theme.c.accentSoft};
        }

        @media (max-width: 960px) {
          .lp-faq-head { grid-template-columns: 1fr !important; gap: 16px !important; }
        }
      `}</style>
    </section>
  );
}
